/**
 * StaffAvailabilityTab — one person's RECURRING weekly availability, and the
 * three surfaces beside it that are declared coming soon.
 *
 * Every window here is real: it reads and writes
 * `/v1/admin/staff/{id}/availability` (contract 2026-09-11) and, in the design
 * environment, the same record the slot resolver reads — so removing a Tuesday
 * window removes Tuesday slots from the booking journey. Nothing on this tab
 * is a local-only edit.
 *
 * BUILT FROM THE DESIGN SYSTEM (2026-09-11 review): Section owns every card
 * shell and its header rhythm, Banner the notices, Field + Dropdown +
 * TimeSelect the editor controls, Table the list, RowActions the per-row
 * cluster, StatusPill the method tags, InlineEmptyState the empty states. The
 * first pass hand-rolled all of those; nothing here re-forks a DS part.
 *
 * NOT `WeeklyHours`, and that is the one deliberate refusal: it is the DS's
 * weekly-pattern editor but it carries `{start,end}` only, and a window here
 * also carries HOW the person can be reached (in person / video / phone) and
 * which services it narrows to. Adopting it would silently drop both on the
 * first save — the seeded video-only Tuesdays would come back as plain hours.
 *
 * What this tab deliberately does NOT offer: a date. A one-off closure or a
 * holiday is an ABSENCE, a different record with a date range, and the
 * Absences tab says so rather than this one growing a field nothing stores.
 */
import { useMemo, useState } from "react";
import {
  Banner, Btn, Dropdown, Field, Icon, IconTile, InlineEmptyState,
  RowActions, Section, StatusPill, Table, TimeSelect, T, formatTimeSelectLabel,
} from "@v0y4ge/jrni-ui";
import { LoadState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { useStaffAvailability, WEEKDAYS, WEEKDAY_LABEL, type AvailabilityRule } from "./useStaffAvailability";

/** Every panel on the person page shares this measure so the tabs line up. */
const PANEL: React.CSSProperties = { width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 14 };

interface DayGroup { day: AvailabilityRule["day"]; rules: AvailabilityRule[] }

function MethodTags({ rule }: { rule: AvailabilityRule }) {
  const t = useT();
  const tags: { key: string; label: string }[] = [];
  if (rule.methods?.inPerson) tags.push({ key: "inPerson", label: t("staffAvailability.inPerson", "In person") });
  if (rule.methods?.video) tags.push({ key: "video", label: t("staffAvailability.video", "Video") });
  if (rule.methods?.phone) tags.push({ key: "phone", label: t("staffAvailability.phone", "Phone") });
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {tags.map((x) => <StatusPill key={x.key} tone="neutral" size="sm">{x.label}</StatusPill>)}
      {rule.services.length ? (
        <StatusPill tone="info" size="sm">
          {rule.services.length + " " + t("staffAvailability.servicesOnly", "services only")}
        </StatusPill>
      ) : null}
    </div>
  );
}

/** The add/edit form for ONE window. Draft state only — the caller commits. */
function RuleEditor({ rule, onCancel, onCommit, busy }: {
  rule: AvailabilityRule;
  onCancel: () => void;
  onCommit: (r: AvailabilityRule) => void;
  busy: boolean;
}) {
  const t = useT();
  const [day, setDay] = useState<AvailabilityRule["day"]>(rule.day);
  const [start, setStart] = useState(rule.start);
  const [end, setEnd] = useState(rule.end);
  /* "How they can be reached" (in person / video / phone) came out of this editor
     on 2026-09-11 at the operator's request — it belongs to MEETING TYPE and will
     be offered there. The stored `methods` are carried through the save untouched,
     so a seeded video-only window stays video-only until meeting type owns it. */
  const badTimes = !(end > start);

  return (
    <Section
      density="content"
      title={rule.id ? t("staffAvailability.editWindow", "Edit window") : t("staffAvailability.addWindow", "Add a window")}
      description={t("staffAvailability.appliesEveryWeek", "Applies every week at the same time.")}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginTop: 14 }}>
        <Field label={t("staffAvailability.day", "Day")}>
          <Dropdown
            value={day}
            onChange={(v) => setDay(v as AvailabilityRule["day"])}
            options={WEEKDAYS.map((d) => ({ value: d, label: WEEKDAY_LABEL[d] }))}
          />
        </Field>
        <TimeSelect label={t("staffAvailability.from", "From")} value={start} onChange={setStart} allowEmpty={false} />
        <TimeSelect label={t("staffAvailability.to", "To")} value={end} onChange={setEnd} allowEmpty={false}
          {...(badTimes ? { wrapperStyle: { outline: "none" } } : {})} />
      </div>

      {badTimes ? (
        <Banner tone="danger" size="compact" style={{ marginTop: 12 }}
          title={t("staffAvailability.endAfterStart", "A window has to end after it starts.")} />
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
        <Btn size="sm" variant="secondary" onClick={onCancel}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn size="sm" disabled={busy || badTimes}
          onClick={() => onCommit({ ...rule, day, start, end })}>
          {busy ? t("staffAvailability.saving", "Saving…") : t("common.actions.save", "Save")}
        </Btn>
      </div>
    </Section>
  );
}

export function StaffAvailabilityTab({ staffId, personName }: { staffId: string | null; personName: string }) {
  const t = useT();
  const av = useStaffAvailability(staffId);
  const [editing, setEditing] = useState<AvailabilityRule | null>(null);

  const groups: DayGroup[] = useMemo(() => WEEKDAYS
    .map((day) => ({ day, rules: av.rules.filter((r) => r.day === day).sort((a, b) => a.start.localeCompare(b.start)) }))
    .filter((g) => g.rules.length), [av.rules]);

  const commit = async (next: AvailabilityRule[], ok: string) => {
    const saved = await av.save(next);
    if (saved) { setEditing(null); toast({ tone: "success", title: ok, subtitle: personName }); }
    else toast({ tone: "danger", title: t("staffAvailability.couldnTSave", "Couldn't save this availability"), subtitle: av.saveError ?? undefined });
  };

  const upsert = (r: AvailabilityRule) => {
    const exists = av.rules.some((x) => x.id === r.id);
    const next = exists ? av.rules.map((x) => (x.id === r.id ? r : x)) : [...av.rules, r];
    void commit(next, exists ? t("staffAvailability.windowUpdated", "Availability updated") : t("staffAvailability.windowAdded", "Availability added"));
  };

  const remove = (r: AvailabilityRule) => {
    void commit(av.rules.filter((x) => x.id !== r.id), t("staffAvailability.windowRemoved", "Availability removed"));
  };

  /* A person with no roster record has no id to hang a pattern on — their
     locations live on the same record, and the Roles tab says the same thing. */
  if (!staffId) {
    return (
      <div style={PANEL}>
        <Section title={t("staffRoles.tabAvailability", "Availability")}
          description={t("staffAvailability.subtitle", "Recurring windows, applied weekly at the same times.")}>
          <InlineEmptyState
            style={{ marginTop: 8 }}
            icon={<IconTile icon={<Icon.calendar size={16} />} />}
            title={t("staffAvailability.noRosterRecord", "No roster record")}
            description={t("staffAvailability.noRosterRecordBody", "Availability is stored on the roster record, and this person doesn't have one — they hold a workspace membership only. Add them to the staff roster to give them a working pattern.")} />
        </Section>
      </div>
    );
  }

  const columns = [
    {
      id: "day",
      header: t("staffAvailability.days", "Days"),
      minWidth: 132,
      cell: (g: DayGroup) => <span style={{ fontWeight: 600, color: T.n1 }}>{WEEKDAY_LABEL[g.day]}</span>,
    },
    {
      id: "time",
      header: t("staffAvailability.time", "Time"),
      minWidth: 220,
      cell: (g: DayGroup) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {g.rules.map((r) => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontVariantNumeric: "tabular-nums", color: T.n1, whiteSpace: "nowrap" }}>
                {formatTimeSelectLabel(r.start)} – {formatTimeSelectLabel(r.end)}
              </span>
              <MethodTags rule={r} />
              <span style={{ flex: 1, minWidth: 0 }} />
              <RowActions
                size={28}
                onEdit={() => setEditing(r)}
                editLabel={t("common.actions.edit", "Edit")}
                moreItems={[{
                  label: t("common.actions.remove", "Remove"),
                  icon: <Icon.x size={13} />,
                  danger: true,
                  onClick: () => remove(r),
                }]}
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div style={PANEL}>
      <Banner
        tone="info"
        size="compact"
        title={av.timezone
          ? t("staffAvailability.tzNote", "Times are shown in this person's own time zone") + " — " + av.timezone + "."
          : t("staffAvailability.tzUnknown", "No time zone is recorded for this person or the workspace, so these windows are stated exactly as written — they are not converted to your zone.")} />

      {editing ? (
        <RuleEditor rule={editing} busy={av.saving} onCancel={() => setEditing(null)} onCommit={upsert} />
      ) : null}

      <Section
        density="dense"
        title={t("staffAvailability.recurring", "Recurring availability")}
        description={t("staffAvailability.recurringBlurb", "These windows apply weekly at the same times.")}
        actions={
          <Btn size="sm" icon={<Icon.plus size={12} />} disabled={av.saving || !!editing}
            onClick={() => setEditing({ id: "", day: "Mon", start: "09:00", end: "17:00", methods: { inPerson: true, video: false, phone: false }, services: [] })}>
            {t("staffAvailability.add", "Add")}
          </Btn>
        }
      >
        <div style={{ marginTop: 12 }}>
          <LoadState loading={av.loading} error={av.error}>
            {!groups.length ? (
              <InlineEmptyState
                icon={<IconTile icon={<Icon.calendar size={16} />} />}
                title={av.seeded
                  ? t("staffAvailability.noWindows", "No recurring availability")
                  : t("staffAvailability.notRecorded", "No pattern recorded yet")}
                description={av.seeded
                  ? t("staffAvailability.noWindowsBody", "This person works no fixed weekly hours, so nothing can be booked with them until a window is added.")
                  : t("staffAvailability.notRecordedBody", "Nobody has said when this person works. Add a window and it becomes bookable time straight away.")} />
            ) : (
              /* The table keeps its columns on a narrow pane by scrolling
                 sideways rather than crushing the time column. */
              <div style={{ overflowX: "auto" }}>
                <Table columns={columns} rows={groups} getRowId={(g) => g.day} />
              </div>
            )}
          </LoadState>
        </div>
      </Section>
    </div>
  );
}

/**
 * A tab that is DECLARED, not drawn. Absences, Calendar and Meeting type all
 * exist as operator intent with no record, no op and no seed behind them yet —
 * so they say exactly that, inert, instead of rendering controls that would
 * discard whatever was typed into them.
 */
export function ComingSoonTab({ title, blurb, note, icon }: {
  title: string;
  blurb: string;
  note: string;
  icon: React.ReactNode;
}) {
  const t = useT();
  return (
    /* INERT, not merely empty: `pointer-events: none` plus `aria-disabled`, so
       nothing on this panel can be clicked, focused or typed into while the
       record behind it does not exist. */
    <div aria-disabled="true" style={{ ...PANEL, pointerEvents: "none", userSelect: "none", opacity: 0.72 }}>
      <Banner tone="neutral" size="compact" title={note} />
      <Section
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            {title}
            <StatusPill tone="neutral" size="sm">{t("staffRoles.comingSoon", "Coming soon")}</StatusPill>
          </span>
        }
        description={blurb}
        actions={
          <Btn size="sm" variant="secondary" disabled icon={<Icon.plus size={12} />}
            title={t("staffRoles.comingSoonDisabled", "Not built yet")}>
            {t("staffAvailability.add", "Add")}
          </Btn>
        }
      >
        {/* What is missing, named on both sides. A surface that says only
            "coming soon" leaves the reader to guess whether it waits on a
            screen or on a record; these two say it outright. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0 4px" }}>
          <StatusPill tone="warning" size="sm">{t("staffRoles.backendPending", "Backend — pending")}</StatusPill>
          <StatusPill tone="warning" size="sm">{t("staffRoles.frontendPending", "Frontend — pending")}</StatusPill>
        </div>
        <InlineEmptyState
          icon={<IconTile icon={icon} />}
          title={title}
          description={t("staffRoles.comingSoonBody", "Nothing is recorded here yet. No record, no operation and no screen exist behind this tab, so it holds no data and accepts none. It is shown disabled on purpose rather than offering controls that would discard what you typed.")} />
      </Section>
    </div>
  );
}
